import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { A11yModule } from '@angular/cdk/a11y';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Requirement, RequirementStatusOption } from '../../models/requirement.model';
import { RequirementsService } from '../../services/requirements.service';
import { RequirementStatusService } from '../../services/requirement-status.service';
import { RequirementStatusLabelPipe } from '../../shared/requirement-status-label.pipe';
import { priorityLabel, resolveStatusKind } from '../../dashboard/requirements/requirements.utils';
import { IconComponent } from '../icon/icon.component';
import { ReqStatusKind, ReqStatusPillComponent } from '../req-status-pill/req-status-pill.component';

type KeyedRequirement = Requirement & { key: string };

interface AuswahlZeile {
  requirement: KeyedRequirement;
  meta: string;
  statusKind: ReqStatusKind;
}

function hasKey(requirement: Requirement): requirement is KeyedRequirement {
  return !!requirement.key;
}

function byKey(a: KeyedRequirement, b: KeyedRequirement): number {
  return a.key.localeCompare(b.key, undefined, { numeric: true });
}

@Component({
  selector: 'app-anforderung-auswahl-dialog',
  imports: [A11yModule, MatIcon, TranslatePipe, IconComponent, ReqStatusPillComponent, RequirementStatusLabelPipe],
  templateUrl: './anforderung-auswahl-dialog.component.html',
  styleUrl: './anforderung-auswahl-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnforderungAuswahlDialogComponent implements OnInit {
  private readonly requirementsService = inject(RequirementsService);
  private readonly statusService = inject(RequirementStatusService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  readonly projectId = input.required<string>();
  readonly excludedRequirementKeys = input<readonly string[]>([]);
  readonly value = input<string | null>(null);
  readonly initialQuery = input('');
  readonly picked = output<Requirement>();
  readonly closed = output<void>();

  private readonly searchField = viewChild<ElementRef<HTMLInputElement>>('suchfeld');
  private readonly dialogElement = viewChild.required<ElementRef<HTMLElement>>('dialog');

  readonly requirements = signal<KeyedRequirement[] | null>(null);
  readonly loading = signal(false);
  readonly loadError = signal(false);
  private readonly statusOptions = signal<RequirementStatusOption[]>([]);
  readonly query = signal('');
  readonly statusFilter = signal('');
  readonly markedKey = signal<string | null>(null);
  private requirementsSubscription: Subscription | null = null;

  readonly available = computed(() => {
    const excluded = new Set(this.excludedRequirementKeys().map((key) => key.trim()));
    return (this.requirements() ?? []).filter((r) => !excluded.has(r.key.trim()));
  });

  readonly statuses = computed(() => {
    const order = new Map(this.statusOptions().map((o) => [o.value, o.orderIndex]));
    const rank = (status: string) => order.get(status) ?? Number.MAX_SAFE_INTEGER;
    return [...new Set(this.available().map((r) => r.status))].sort((a, b) => rank(a) - rank(b));
  });

  readonly rows = computed<AuswahlZeile[]>(() => {
    const term = this.query().trim().toLowerCase();
    const status = this.statusFilter();
    const options = this.statusOptions();
    return this.available()
      .filter((r) => !status || r.status === status)
      .filter((r) => !term || `${r.key} ${r.title} ${r.authorUsername ?? ''}`.toLowerCase().includes(term))
      .map((requirement) => ({
        requirement,
        meta: [requirement.type, priorityLabel(requirement.priority, this.translate), requirement.authorUsername]
          .filter((part) => !!part)
          .join(' · '),
        statusKind: resolveStatusKind(requirement.status, options),
      }));
  });

  readonly projectEmpty = computed(() => this.requirements()?.length === 0);
  readonly allLinked = computed(() => !this.projectEmpty() && this.available().length === 0);
  readonly markedRequirement = computed(() => this.available().find((r) => r.key === this.markedKey()) ?? null);
  readonly activeOptionId = computed(() => {
    const key = this.markedKey();
    return key && this.rows().some((row) => row.requirement.key === key) ? this.optionId(key) : null;
  });

  constructor() {
    afterNextRender(() => this.focusStart());
  }

  ngOnInit(): void {
    this.markedKey.set(this.value());
    this.query.set(this.initialQuery());
    this.statusService
      .list(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (options) => this.statusOptions.set(options),
        error: () => this.statusOptions.set([]),
      });
    this.load();
  }

  load(): void {
    this.requirementsSubscription?.unsubscribe();
    this.loading.set(true);
    this.loadError.set(false);
    this.requirementsSubscription = this.requirementsService
      .list(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (requirements) => {
          this.requirements.set(requirements.filter(hasKey).sort(byKey));
          this.loading.set(false);
          afterNextRender(() => this.focusSearchIfDialogHasFocus(), { injector: this.injector });
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  optionId(key: string): string {
    return `anf-auswahl-option-${key}`;
  }

  onSearch(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }

  onStatusChange(event: Event): void {
    this.statusFilter.set((event.target as HTMLSelectElement).value);
  }

  mark(requirement: KeyedRequirement): void {
    this.markedKey.set(requirement.key);
  }

  pick(requirement: Requirement | null = this.markedRequirement()): void {
    if (requirement) {
      this.picked.emit(requirement);
    }
  }

  close(): void {
    this.closed.emit();
  }

  onEscape(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.close();
  }

  onListKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveMark(1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        this.moveMark(-1);
        return;
      case ' ': {
        event.preventDefault();
        const row = this.rows().find((r) => r.requirement.key === this.markedKey()) ?? this.rows()[0];
        if (row) this.mark(row.requirement);
        return;
      }
      case 'Enter':
        event.preventDefault();
        this.pick();
        return;
    }
  }

  private moveMark(delta: number): void {
    const rows = this.rows();
    if (!rows.length) return;
    const current = rows.findIndex((row) => row.requirement.key === this.markedKey());
    const next =
      current < 0 ? (delta > 0 ? 0 : rows.length - 1) : Math.min(Math.max(current + delta, 0), rows.length - 1);
    this.mark(rows[next].requirement);
    this.dialogElement()
      .nativeElement.querySelector(`#${CSS.escape(this.optionId(rows[next].requirement.key))}`)
      ?.scrollIntoView({ block: 'nearest' });
  }

  private focusStart(): void {
    const search = this.searchField()?.nativeElement;
    if (search && !search.disabled) {
      search.focus();
    } else {
      this.dialogElement().nativeElement.focus();
    }
  }

  private focusSearchIfDialogHasFocus(): void {
    if (document.activeElement === this.dialogElement().nativeElement) {
      this.searchField()?.nativeElement.focus();
    }
  }
}
